import { createFileRoute } from "@tanstack/react-router";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import {
  ProductStatusOptions,
  ProductTableColumns,
} from "@/components/product/product-meta";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { Package } from "lucide-react";
import type { Product } from "@/store/Product";
import { ProductModals } from "@/components/product/product-modals";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import { Plus } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import useAppStore from "@/store";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import TableSettings from "@/components/ui/shared/table-settings";

export const Route = createFileRoute("/(dasboard)/products/products/")({
  component: ProductsPage,
});

function ProductsPage() {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const store = useAppStore();
  const tableName = "products";
  const tableAttributes = useTableAttributes();

  const searchForm = useSimpleForm({
    keyword: "",
    category: undefined,
    type: undefined,
    status: undefined, // For filtering by status
  });

  const search = ({ page = 1, per_page = 10 } = {}) => {
    store.product.fetch({ page, per_page, ...searchForm.values });
  };

  const handleAutoComplete = (values: any) => {
    store.product.paginate(
      store.product.items.filter((item: Product) => {
        const nameMatch = item.name
          .toLowerCase()
          .includes(values.keyword.toLowerCase());

        const statusMatch = values.status
          ? item.status === values.status
          : true;

        const categoryMatch = values.category
          ? item.category_id === values.category
          : true;

        const typeMatch = values.type ? item.type_id === values.type : true;

        return nameMatch && statusMatch && categoryMatch && typeMatch;
      })
    );
  };

  useEffect(() => {
    if (store.auth.session) {
      search({});
      store.category.fetch();
    }

    tableAttributes.loadAttributes(tableName, [
      "product_name",
      "category",
      "selling_price",
      "quantity",
      "status",
      "creator",
      "actions",
    ]);
  }, [store.auth.session]);

  const columns = ProductTableColumns({
    store,
    onEdit: (item: any) => {
      store.product.setCurrent(item);
      navigate({ to: `/products/products/${item.id}` });
    },
    onDelete: async (item: any) => {
      store.product.setCurrent(item);
      store.display.show("delete_product");
    },
  }).filter((item) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  return (
    <div>
      <header className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold"> {t("Products")} </h2>
        <Button
          onClick={() => {
            navigate({ to: "/products/products/create" });
          }}
        >
          <Plus className="w-4 h-4" />
          {t("Add product")}
        </Button>
      </header>
      <ProductModals />

      <TableSettings
        columns={ProductTableColumns({ store })}
        tableName={tableName}
      >
        <SearchEngine
          autoComplete={handleAutoComplete}
          selects={[
            {
              placeholder: t("All statuses"),
              options: ProductStatusOptions,
              name: "status",
              icon: <Package className="w-4 h-4" />,
            },
            {
              placeholder: t("All categories"),
              options: store.category.items.map((item) => ({
                value: item.id,
                label: item.name,
              })),
              name: "category",
              icon: <Package className="w-4 h-4" />,
              size: "w-full",
            },
          ]}
          form={searchForm}
          onSubmit={() => search()} // Trigger full fetch on form submit
        />
      </TableSettings>

      <SimpleTable items={store.product.pagination.data} columns={columns} />

      <FullPagination
        className="mt-4"
        canEdit={true}
        pagination={store.product.pagination}
        onGoto={(page: number) =>
          store.product.paginate(store.product.result, { page })
        }
        onPerPage={(per_page) =>
          store.product.paginate(store.product.result, { per_page })
        }
      />
    </div>
  );
}
